import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Button,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../context/ToastContext';
import ConfirmDialog from '../common/ConfirmDialog';
import { BRAND } from '../../theme/brandTokens';

const cloneDayHours = (hours) => ({
  closed: hours.closed,
  periods: hours.periods.map((period) => ({ ...period })),
});

const formatDayHoursSummary = (hours, t) => {
  if (hours.closed) {
    return t('common.closed');
  }

  return hours.periods.map((period) => `${period.from} - ${period.to}`).join(', ');
};

const validateDayPeriods = (periods, showError, t) => {
  for (const period of periods) {
    if (period.from >= period.to) {
      showError(t('storeRegistration.validation.openTimeAfterCloseTime'));
      return false;
    }
  }

  const sortedPeriods = [...periods].sort((left, right) => left.from.localeCompare(right.from));
  for (let index = 0; index < sortedPeriods.length - 1; index += 1) {
    if (sortedPeriods[index].to > sortedPeriods[index + 1].from) {
      showError(t('storeRegistration.validation.periodsOverlap'));
      return false;
    }
  }

  return true;
};

const StoreBusinessHoursInfo = ({ formData, updateFormData, errors }) => {
  const { t } = useTranslation();
  const { showError } = useToast();
  const [applyDialog, setApplyDialog] = useState({
    open: false,
    sourceDay: null,
    sourceHours: null,
    fieldLabel: '',
    valueLabel: '',
  });

  const closeApplyDialog = () => {
    setApplyDialog({
      open: false,
      sourceDay: null,
      sourceHours: null,
      fieldLabel: '',
      valueLabel: '',
    });
  };

  const openApplyDialog = (sourceDay, sourceHours, fieldLabel, valueLabel) => {
    setApplyDialog({
      open: true,
      sourceDay,
      sourceHours: cloneDayHours(sourceHours),
      fieldLabel,
      valueLabel,
    });
  };

  const applySourceHoursToAllDays = (sourceHours) => {
    const periodsToApply = cloneDayHours(sourceHours).periods;
    const updatedBusinessHours = Object.fromEntries(
      Object.entries(formData.businessHours).map(([day, hours]) => {
        if (hours.closed) {
          return [day, cloneDayHours(hours)];
        }

        return [day, { closed: false, periods: periodsToApply.map((period) => ({ ...period })) }];
      })
    );

    updateFormData({ businessHours: updatedBusinessHours });
  };

  const hasOtherOpenDays = (sourceDay) => (
    Object.entries(formData.businessHours).some(([day, hours]) => day !== sourceDay && !hours.closed)
  );

  const updateDayHours = (day, nextDayHours, fieldLabel, valueLabel) => {
    updateFormData({
      businessHours: {
        ...formData.businessHours,
        [day]: nextDayHours,
      },
    });

    if (!nextDayHours.closed && hasOtherOpenDays(day)) {
      openApplyDialog(day, nextDayHours, fieldLabel, valueLabel);
    }
  };

  const generateTimeOptions = () => {
    const options = [];

    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 10) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({ value: timeString, label: timeString });
      }
    }

    return options;
  };

  const timeOptions = generateTimeOptions();

  const handlePeriodChange = (day, periodIndex, field, value) => {
    const dayHours = formData.businessHours[day];
    const newPeriods = dayHours.periods.map((period, index) => (
      index === periodIndex ? { ...period, [field]: value } : period
    ));

    if (!validateDayPeriods(newPeriods, showError, t)) {
      return;
    }

    const nextDayHours = { ...dayHours, periods: newPeriods };
    const fieldLabel = field === 'from'
      ? t('storeRegistration.businessHours.openTime')
      : t('storeRegistration.businessHours.closeTime');

    updateDayHours(day, nextDayHours, fieldLabel, value);
  };

  const handleDayOpenChange = (day, hours, isOpen) => {
    updateFormData({
      businessHours: {
        ...formData.businessHours,
        [day]: { ...hours, closed: !isOpen },
      },
    });
  };

  const handleAddPeriod = (day) => {
    const dayHours = formData.businessHours[day];
    const lastPeriod = dayHours.periods[dayHours.periods.length - 1];
    const newFrom = lastPeriod?.to || '09:00';
    const fromIndex = timeOptions.findIndex((option) => option.value === newFrom);
    const toValue = fromIndex >= 0 && fromIndex + 6 < timeOptions.length
      ? timeOptions[fromIndex + 6].value
      : '18:00';
    const newPeriods = [...dayHours.periods, { from: newFrom, to: toValue }];

    if (!validateDayPeriods(newPeriods, showError, t)) {
      return;
    }

    const nextDayHours = {
      ...dayHours,
      periods: newPeriods,
    };

    updateDayHours(
      day,
      nextDayHours,
      t('storeRegistration.businessHours.title'),
      formatDayHoursSummary(nextDayHours, t)
    );
  };

  const handleRemovePeriod = (day, periodIndex) => {
    const dayHours = formData.businessHours[day];
    if (dayHours.periods.length <= 1) {
      return;
    }

    const nextDayHours = {
      ...dayHours,
      periods: dayHours.periods.filter((_, index) => index !== periodIndex),
    };

    updateDayHours(
      day,
      nextDayHours,
      t('storeRegistration.businessHours.title'),
      formatDayHoursSummary(nextDayHours, t)
    );
  };

  const handleApplyToAllDays = () => {
    if (applyDialog.sourceHours) {
      applySourceHoursToAllDays(applyDialog.sourceHours);
    }
    closeApplyDialog();
  };

  return (
    <Box id="business-hours-step">
      <Typography variant="h5" sx={{ color: BRAND.text, fontWeight: 700, mb: 3 }}>
        {t('storeRegistrationSteps.step4.title')}
      </Typography>

      <Typography variant="h6" sx={{ color: BRAND.text, fontWeight: 600, pt: 1, mb: 2 }}>
        {`${t('storeRegistration.businessHours.title')} *`}
      </Typography>

      <Box
        id="business-hours-section"
        sx={{
          border: `1px solid ${BRAND.line}`,
          borderRadius: 2,
          backgroundColor: BRAND.background,
        }}
      >
        {Object.entries(formData.businessHours).map(([day, hours]) => (
          <Box
            key={day}
            id={`business-hours-day-${day}`}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'flex-start' },
              gap: { xs: 1, sm: 2 },
              p: 2,
              borderBottom: `1px solid ${BRAND.lineSoft}`,
              backgroundColor: BRAND.surface,
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <FormControlLabel
              control={(
                <Checkbox
                  id={`business-hours-checkbox-${day}`}
                  checked={!hours.closed}
                  onChange={(event) => handleDayOpenChange(day, hours, event.target.checked)}
                  sx={{
                    color: BRAND.accentText,
                    '&.Mui-checked': { color: BRAND.accentText },
                  }}
                />
              )}
              label={(
                <Typography
                  id={`business-hours-day-label-${day}`}
                  sx={{
                    fontWeight: 600,
                    color: hours.closed ? 'text.disabled' : BRAND.text,
                    width: { xs: 'auto', sm: 110 },
                    minWidth: { xs: 'auto', sm: 110 },
                    textTransform: 'capitalize',
                  }}
                >
                  {t(`common.days.${day}`)}
                </Typography>
              )}
            />

            {hours.closed ? (
              <Typography variant="body2" color="text.disabled" sx={{ alignSelf: 'center', fontStyle: 'italic' }}>
                {t('common.closed')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 140, minWidth: 140, textAlign: 'center' }}>
                    {t('storeRegistration.businessHours.openTime')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 140, minWidth: 140, textAlign: 'center' }}>
                    {t('storeRegistration.businessHours.closeTime')}
                  </Typography>
                </Box>

                {hours.periods.map((period, periodIndex) => (
                  <Box key={`${day}-${period.from}-${period.to}-${periodIndex}`} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <Select
                      value={period.from}
                      onChange={(event) => handlePeriodChange(day, periodIndex, 'from', event.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      size="small"
                      sx={{ width: 140, minWidth: 140 }}
                    >
                      {timeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>

                    <Select
                      value={period.to}
                      onChange={(event) => handlePeriodChange(day, periodIndex, 'to', event.target.value)}
                      MenuProps={{ disableScrollLock: true }}
                      size="small"
                      sx={{ width: 140, minWidth: 140 }}
                    >
                      {timeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>

                    {hours.periods.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePeriod(day, periodIndex)}
                        title={t('storeRegistration.businessHours.removePeriod')}
                        sx={{ color: '#c0392b', ml: 0.5 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}

                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddPeriod(day)}
                  sx={{
                    alignSelf: 'flex-start',
                    color: BRAND.accentText,
                    fontWeight: 600,
                    textTransform: 'none',
                    mt: 0.5,
                    px: 1,
                  }}
                >
                  {t('storeRegistration.businessHours.addPeriod')}
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {errors.businessHours && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.businessHours}
        </Alert>
      )}

      <ConfirmDialog
        open={applyDialog.open}
        onClose={closeApplyDialog}
        onConfirm={handleApplyToAllDays}
        title={t('storeRegistration.businessHours.applyToAllTitle')}
        message={t('storeRegistration.businessHours.applyToAllMessage', {
          field: applyDialog.fieldLabel,
          day: applyDialog.sourceDay ? t(`common.days.${applyDialog.sourceDay}`) : '',
          value: applyDialog.valueLabel,
        })}
        cancelText={t('storeRegistration.businessHours.applyToCurrentDay', {
          day: applyDialog.sourceDay ? t(`common.days.${applyDialog.sourceDay}`) : '',
        })}
        confirmText={t('storeRegistration.businessHours.applyToAllDays')}
        confirmColor="primary"
      />
    </Box>
  );
};

export default StoreBusinessHoursInfo;